import { Button, Col, DatePicker, Layout, Row, Space, Spin, Typography } from 'antd';
import { Select } from 'antd';
import React, { useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { isEmpty } from 'underscore';
import { IPlotVariable } from '../../../../typings/analytics';
import styles from '../../Style/LayoutPanel.module.scss';
import { ChartBox } from '../ChartBox';
import { IBestDataset } from '../../../../typings/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import moment, { Moment } from 'moment';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import IconPoint from '../../../../assets/icon/locate2.png'
import CustomDivider from '../CustomDIvider';
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function Statistics(props: {
  coordinates: number[];
  previewDatasetId: string;
  datasets: IBestDataset[];
  dateRange: [moment.Moment, moment.Moment];
  onDatasetChange: (item: string) => void
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  statisticsImages: string;
  statisticsValuesLinks: string[];
  error: string;
  pointName: string;
  updating: boolean
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem
  listNavigation: ReactNode
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
}) {
  const {
    datasets,
    dateRange,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    onUpdate,
    statisticsImages,
    statisticsValuesLinks,
    error,
    pointName,
    userInput,
    updateUserInput,
    updating,
    onDatasetChange,
    listNavigation,
    point,
    onChangeActiveKey,
    pointOption
  } = props;
  const [listVariablesX, setListVariableX] = useState<IPlotVariable[]>([]);
  const [dataset, setDataset] = useState(userInput.Statistics.dataset);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.Statistics.variable
  );
  const [startDate, setStartDate] = useState(
    userInput.Statistics.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.Statistics.endDate || moment(dateRange[1])
  );

  useEffect(() => {
    updateUserInput({
      Statistics: {
        dataset: dataset,
        variable: variableX,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variableX, startDate, endDate]);

  const getData = useCallback(
    (
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.STATISTICS,
          DatasetId: previewDatasetId,
          OriginalDatasetId: previewDatasetId,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, previewDatasetId, pointName]
  );

  useEffect(() => {
    if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && statisticsImages === '') {
      setDataset(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === props.coordinates.join()
        );
        if (task && task.ItemsInfo) {
          const itemsList = task.ItemsInfo.filter(
            (item) => !item.Name.toLowerCase().includes('direction') && !item.Name.toLowerCase().includes('directional'))
          setListVariableX(itemsList);
          const indexX = itemsList[0];
          setVariableX(indexX);
          getData(
            indexX,
            task.OrderId,
            startDate.format(DT0Format),
            endDate.format(DT0Format)
          );
        }
      }
    }
  }, [previewDatasetId, datasets, enrichedMetadata, coordinates, statisticsImages]);

  const onUpdateStatistics = useCallback(
    () => {
      if (enrichedMetadata && variableX) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === dataset &&
            task.CoordinatesString === props.coordinates.join()
        );

        if (task) {
          getData(
            variableX,
            task.OrderId,
            startDate.format(DT0Format),
            endDate.format(DT0Format),
          );
        }
      }
    },
    [enrichedMetadata, dataset, startDate, endDate, variableX, pointName]
  );

  const doVariableChange = (active: number) => {
    const item = listVariablesX.find((e) => e.Index === active)
    setVariableX(item!);
  };

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value)
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onSelectDataset = (value: string) => {
    onDatasetChange(value)
  }

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day');
  }

  const categories = useMemo(() => {
    const label = props.datasets.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [props.datasets])

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={onChangeActiveKey}
                options={pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  onChange={onSelectDataset}
                  disabled={updating}
                >
                  {categories.map((category: string) => (
                    <Select.OptGroup key={category} label={category}>
                      {datasets
                        .filter((item) => item.label === category)
                        .map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Col>
              <Col span={24} md={6} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px', minWidth: 220 }}
                  placeholder="Select variable"
                  onChange={doVariableChange}
                  value={variableX?.Index}
                  disabled={updating}
                >
                  {listVariablesX
                    .filter((item) => item.Name !== 'Time')
                    .map((item) => {
                      return (
                        <Select.Option key={item.Index} value={item.Index}>
                          {item.Name}
                        </Select.Option>
                      );
                  })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              disabled={updating}
              loading={updating}
              onClick={onUpdateStatistics}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
        {!updating && error && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{error}</div>}
        {!updating && !error && (
            <ChartBox
              actionRow={<></>}
              key={'Scatter Table'}
              title={'Scatter Table'}
              height={updating || !isEmpty(error) ? '50px' : 'auto'}
              plot={
                updating ? (
                  <div className={styles.spinner}>
                    <Spin size="large" />
                  </div>
                ) : !isEmpty(error) ? (
                  <div>{error}</div>
                ) : statisticsImages && (
                  <img
                    style={{ width: '80%', objectFit: 'scale-down', maxHeight: '65vh' }}
                    src={statisticsImages}
                    alt="Statistics"
                  />
                )
              }
              dataset={''}
              showDisclaimer
              link={statisticsValuesLinks}
            />
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        />
      </Layout>
    </Layout>
  );
}
